import { nextTick, type Ref, ref, watch } from 'vue';

import { toElement } from '../../utils/templateRefNarrowing';
import { DATA_ATTRIBUTES, FADE_ANIMATION_DURATION } from './constants';

/** Accept Ref<unknown> so untyped useTemplateRef() can be passed. */
interface UseVisibleElementsWidthOptions {
  actionsContainerRef: Ref<unknown>;
  overflowIds: Ref<Set<string>>;
}

/**
 * Computes the total width of currently visible MoreActions items from live DOM layout.
 * Use from mounted client components only; does not run on the server.
 */
export function useVisibleElementsWidth({ actionsContainerRef, overflowIds }: UseVisibleElementsWidthOptions) {
  const visibleElementsWidth = ref(0);
  const isRecalculatingWidth = ref(false);
  const isRecalculatingWithDelay = ref(false);
  let widthCalculationTimeout: ReturnType<typeof setTimeout> | null = null;

  /**
   * Calculates the total width of currently visible elements
   * This is used for positioning the More button
   */
  const calculateVisibleElementsWidth = () => {
    const container = toElement(actionsContainerRef.value);
    if (!container) {
      visibleElementsWidth.value = 0;
      return;
    }

    let totalWidth = 0;
    const directChildren = Array.from(container.children);

    directChildren.forEach((element, index) => {
      const elementId = element.getAttribute(DATA_ATTRIBUTES.ACTION_ID);

      // Check if element should be visible based on overflow state
      // Elements without data-action-id are always considered visible (never hidden by overflow logic)
      const shouldBeVisible = !elementId || !overflowIds.value.has(elementId);

      // Double-check with CSS classes as a fallback
      const isHiddenByCSS = element.classList.contains('invisible');

      // Element is considered visible if:
      // 1. It's not in overflowIds (should be visible) OR it has no data-action-id (always visible)
      // 2. AND it's not hidden by CSS classes
      if (shouldBeVisible && !isHiddenByCSS) {
        const rect = element.getBoundingClientRect();
        totalWidth += rect.width;

        // Add gap between elements
        if (index < directChildren.length - 1 && container) {
          // Always get the most current gap width from CSS computed styles of the actions container
          const computedStyle = window.getComputedStyle(container);
          const gapValue = computedStyle.columnGap || computedStyle.gap;
          const gapWidth = gapValue ? parseFloat(gapValue) : 0;
          totalWidth += gapWidth;
        }
      }
    });

    visibleElementsWidth.value = totalWidth;
  };

  /**
   * Calculates visible elements width with a delay to allow animations to complete
   * This ensures that width calculation happens after elements are actually hidden/shown
   */
  const calculateVisibleElementsWidthDelayed = () => {
    // Set recalculating state immediately to hide More button
    isRecalculatingWithDelay.value = true;

    // Clear any existing timeout
    if (widthCalculationTimeout) {
      clearTimeout(widthCalculationTimeout);
    }

    // Set a new timeout to calculate width after animation completes
    widthCalculationTimeout = setTimeout(() => {
      if (!isRecalculatingWidth.value) {
        isRecalculatingWidth.value = true;

        // Use double nextTick to ensure all DOM updates and CSS changes are applied
        nextTick(() => {
          nextTick(() => {
            calculateVisibleElementsWidth();
            isRecalculatingWidth.value = false;
            isRecalculatingWithDelay.value = false; // Show More button after calculation is complete
          });
        });
      }
    }, FADE_ANIMATION_DURATION + 100); // Add small buffer to ensure animation is complete
  };

  // Watch for changes in overflowIds to recalculate visible elements width with delay
  watch(overflowIds, () => {
    calculateVisibleElementsWidthDelayed();
  });

  /**
   * Cleanup function to clear any pending timeouts
   */
  const cleanup = () => {
    if (widthCalculationTimeout) {
      clearTimeout(widthCalculationTimeout);
      widthCalculationTimeout = null;
    }
    isRecalculatingWithDelay.value = false;
  };

  return {
    visibleElementsWidth,
    isRecalculatingWidth,
    isRecalculatingWithDelay,
    calculateVisibleElementsWidth,
    calculateVisibleElementsWidthDelayed,
    cleanup,
  };
}
